import { Info } from 'lucide-react'
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import { AddressAutocomplete } from '@components/ui/AddressAutocomplete'
import { formatAmount } from '@/lib/utils/formatters'
import {
  formatCardNumber,
  formatCVV,
  getExpiryMonthOptions,
  getExpiryYearOptions,
  sanitizePartialPaymentInput,
} from './paymentFormFormatters'
import type { PaymentFormHookState } from './usePaymentForm'

const monthOptions = getExpiryMonthOptions()
const yearOptions = getExpiryYearOptions()

export function PaymentCreditCardForm({
  form,
  isLoading,
}: {
  form: PaymentFormHookState
  isLoading: boolean
}) {
  const {
    paymentType,
    handleSubmit,
    isContractPayment,
    paymentFee,
    remainingAmount,
    usePartialPayment,
    paymentAmount,
    handlePaymentAmountChange,
    paymentAmountError,
    productFee,
    totalAmount,
    allowPartialPayment,
    setUsePartialPayment,
    setPrepaidCard,
    setPaymentAmount,
    setPaymentAmountError,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    isAutoFilled,
    errors,
    setShowNameInfoModal,
    billingAddressDifferent,
    setBillingAddressDifferent,
    prepaidCard,
    isPoBox,
    setIsPoBox,
    billingAddress,
    setBillingAddress,
    handleAddressSelect,
    streetTypes,
    showAptTooltip,
    setShowAptTooltip,
    cardNumber,
    setCardNumber,
    expiryMonth,
    setExpiryMonth,
    expiryYear,
    setExpiryYear,
    cvv,
    setCvv,
    states,
  } = form

  return (
<form onSubmit={handleSubmit} className="space-y-6">
  {/* Header */}
  <div className="flex items-center justify-between mb-6">
    <h2 className="text-2xl font-bold text-gray-800">CREDIT CARD</h2>
    <img
      src="/PaymentLogo.png"
      alt="Payment Methods"
      className="h-16 w-auto object-contain"
      loading="lazy"
      onError={(e) => {
        // Hide image if it fails to load
        const target = e.target as HTMLImageElement
        target.style.display = 'none'
      }}
    />
  </div>

  {/* Payment Info Container - Side by Side */}
  <div className="flex flex-col xl:flex-row gap-6 mb-6">
    {/* Payment Summary */}
    <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-5">
      <h3 className="font-semibold text-gray-900 mb-3">
        {paymentType === 'APPLICATION_FEE'
          ? 'Liberty Rent - Application Fee'
          : paymentType === 'CONTRACT'
            ? 'Liberty Rent - Access Fee'
            : 'Liberty Rent - Payment Due'}
      </h3>
      <div className="space-y-2 text-sm">
        {isContractPayment && paymentFee ? (
          <>
            <div className="flex justify-between">
              <span className="text-gray-700">Total Access Fee:</span>
              <span className="font-semibold text-gray-900">{formatAmount(remainingAmount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Service Fee :</span>
              <span className="font-semibold text-gray-900">{paymentFee.processing_fee > 0 ? formatAmount(paymentFee.processing_fee) : '$0.00'}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-blue-200">
              <span className="font-semibold text-gray-900">Total Fee Paying:</span>
              <span className="font-bold text-lg text-gray-900">{formatAmount(paymentFee.total_amount)}</span>
            </div>

            {/* Payment Amount Input - Show inside Access Fee box when partial payment is enabled, below Total Fee Paying */}
            {usePartialPayment && (
              <div className="pt-3 pb-3 border-t border-blue-200">
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Amount* (you can pay partial payment)
                </label>
                <div className="flex justify-end">
                  <div className="w-full max-w-xs">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={paymentAmount}
                      onChange={(e) =>
                        handlePaymentAmountChange(sanitizePartialPaymentInput(e.target.value))
                      }
                      placeholder={remainingAmount > 0 ? remainingAmount.toFixed(2) : '0.00'}
                      className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right ${paymentAmountError ? 'border-red-500' : 'border-gray-300'
                        }`}
                      onWheel={(e) => e.currentTarget.blur()}
                      required={isContractPayment && usePartialPayment}
                    />
                    <style>{`
                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button {
                  -webkit-appearance: none;
                  margin: 0;
                }
              `}</style>
                    {paymentAmountError && (
                      <p className="mt-1 text-sm text-red-600 text-right">{paymentAmountError}</p>
                    )}
                    {remainingAmount > 0 && (
                      <p className="mt-1 text-xs text-gray-500 text-right">
                        Remaining balance: {formatAmount(remainingAmount - (paymentAmount ? Number(paymentAmount) : 0))}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex justify-between">
              <span className="text-gray-700">Product Fee:</span>
              <span className="font-semibold text-gray-900">{formatAmount(productFee !== null ? productFee : 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-700">Service Fee:</span>
              <span className="font-semibold text-gray-900">Free</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-blue-200">
              <span className="font-semibold text-gray-900">Total Amount:</span>
              <span className="font-bold text-lg text-gray-900">{formatAmount(totalAmount !== null ? totalAmount : 0)}</span>
            </div>
          </>
        )}
      </div>
    </div>

    {/* Partial Payment Method Button - Contract only (default invoice must be paid in full) */}
    {allowPartialPayment && !usePartialPayment && (
      <div className="flex-1">
        <div className="bg-white border border-gray-200 rounded-lg p-4 h-full">
          <h4 className="font-semibold text-gray-900 mb-2">Multiple Pre-paid Debit Cards?</h4>
          <p className="text-sm text-gray-700 mb-4">
            If you need to spread your contract payment across multiple pre-loaded cards, then use this option. This is not to be used for credit cards, and should only be used if you have the cash available. Once the Balance Due is satisfied and funds are captured, then we will send the Liberty Rent Insurance Policy to your new Community where you can continue with your Lease.
          </p>
          <button
            type="button"
            onClick={() => {
              setUsePartialPayment(true)
              setPrepaidCard(true) // Set prepaid_card to true when using partial payment (per documentation)
              setPaymentAmount(remainingAmount.toFixed(2))
            }}
            className="px-6 py-2 bg-[#1597ff] text-white font-medium rounded-lg hover:bg-[#0d7acc] transition-colors"
          >
            Use Partial Payment Method
          </button>
        </div>
      </div>
    )}
  </div>

  {/* Full Payment Method Button when Partial Payment is Active */}
  {isContractPayment && usePartialPayment && (
    <div className="mb-6 flex justify-end">
      <button
        type="button"
        onClick={() => {
          setUsePartialPayment(false)
          setPrepaidCard(false) // Reset prepaid_card when switching to full payment
          setPaymentAmount('')
          setPaymentAmountError('')
        }}
        className="px-6 py-2 bg-[#1597ff] text-white font-medium rounded-lg hover:bg-[#0d7acc] transition-colors"
      >
        Use Full Payment Method
      </button>
    </div>
  )}

  {/* Name Fields */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">First Name*</label>
      <input
        type="text"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        disabled={isAutoFilled.firstName}
        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.firstName ? 'border-red-500' : 'border-gray-300'
          } ${isAutoFilled.firstName ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
        required
      />
      {errors.firstName && (
        <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
      )}
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Last Name*</label>
      <input
        type="text"
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
        disabled={isAutoFilled.lastName}
        className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.lastName ? 'border-red-500' : 'border-gray-300'
          } ${isAutoFilled.lastName ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
        required
      />
      {errors.lastName && (
        <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
      )}
    </div>
  </div>

  <div className="mb-4">
    <button
      type="button"
      onClick={() => setShowNameInfoModal(true)}
      className="text-sm text-blue-600 hover:text-blue-800 underline"
    >
      Why can't I change my name?
    </button>
  </div>

  {/* Billing Address Section */}
  <div className="mt-8">
    <h3 className="text-lg font-semibold text-gray-800 mb-4">BILLING ADDRESS</h3>

    <div className="space-y-4 mb-6">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={billingAddressDifferent}
          onChange={(e) => {
            setBillingAddressDifferent(e.target.checked)
            if (!e.target.checked) {
              setIsPoBox(false)
              setBillingAddress((prev) => ({ ...prev, address_type: 'STREET' }))
            }
          }}
          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">
          Click if billing address is different than your current address on application
        </span>
      </label>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={prepaidCard}
          onChange={(e) => setPrepaidCard(e.target.checked)}
          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-gray-700">Click if pre-paid card</span>
      </label>

      {billingAddressDifferent && !prepaidCard && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isPoBox}
            onChange={(e) => {
              setIsPoBox(e.target.checked)
              setBillingAddress((prev) => ({
                ...prev,
                address_type: e.target.checked ? 'POBOX' : 'STREET',
                // Clear apartment/box number when unchecking PO Box (matches Angular isPOBox())
                ...(!e.target.checked && { apartment: '' }),
              }))
            }}
            className="w-4 h-4 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">USPS PO Box</span>
        </label>
      )}
    </div>

    {/* Google Places Autocomplete */}
    {billingAddressDifferent && !prepaidCard && (
      <div className="mb-6">
        <AddressAutocomplete
          value={billingAddress.payment_address || ''}
          onChange={(val) => setBillingAddress(prev => ({ ...prev, payment_address: val }))}
          onPlaceSelect={handleAddressSelect}
          placeholder="Enter your address"
          label={APPLICANT_CONTENT.application.sections.currentAddress.address}
        />
      </div>
    )}

    {/* Address Fields */}
    {/* When PO Box is checked: Street No and Street are hidden; apartment label changes to "PO Box" */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
      {!isPoBox && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Street No*</label>
          <input
            type="text"
            value={billingAddress.house_no}
            onChange={(e) => setBillingAddress({ ...billingAddress, house_no: e.target.value })}
            disabled={!billingAddressDifferent}
            className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.house_no ? 'border-red-500' : 'border-gray-300'
              } ${!billingAddressDifferent ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            required
          />
          {errors.house_no && (
            <p className="mt-1 text-sm text-red-600">{errors.house_no}</p>
          )}
        </div>
      )}

      {!isPoBox && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Street*</label>
          <input
            type="text"
            value={billingAddress.street}
            onChange={(e) => setBillingAddress({ ...billingAddress, street: e.target.value })}
            disabled={!billingAddressDifferent}
            className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.street ? 'border-red-500' : 'border-gray-300'
              } ${!billingAddressDifferent ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            required
          />
          {errors.street && (
            <p className="mt-1 text-sm text-red-600">{errors.street}</p>
          )}
        </div>
      )}

      {!isPoBox && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Street Type</label>
          <select
            value={billingAddress.street_type || ''}
            onChange={(e) => setBillingAddress({ ...billingAddress, street_type: e.target.value })}
            disabled={!billingAddressDifferent}
            className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!billingAddressDifferent ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
          >
            <option value="">Street Type</option>
            {streetTypes.map(st => (
              <option key={st.street_suffix_code} value={st.street_suffix_code}>
                {st.street_suffix}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isPoBox ? 'PO Box' : 'Apt/Unit or Box#'}
          <span className="relative inline-block ml-1">
            <Info
              className="inline w-4 h-4 text-green-600 cursor-pointer"
              onMouseEnter={() => setShowAptTooltip(true)}
              onMouseLeave={() => setShowAptTooltip(false)}
            />
            {showAptTooltip && (
              <div className="absolute left-0 top-6 z-10 w-64 bg-white border border-gray-200 rounded shadow-lg p-2 text-xs text-gray-700 font-normal">
                Some addresses (ex. UPS Store Boxes) have a full street address and box number. Input the street address and your personal box number here
              </div>
            )}
          </span>
        </label>
        <input
          type="text"
          value={billingAddress.apartment || ''}
          onChange={(e) => setBillingAddress({ ...billingAddress, apartment: e.target.value })}
          disabled={!billingAddressDifferent}
          className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!billingAddressDifferent ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
            }`}
        />
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">City*</label>
        <input
          type="text"
          value={billingAddress.city}
          onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
          disabled={!billingAddressDifferent}
          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.city ? 'border-red-500' : 'border-gray-300'
            } ${!billingAddressDifferent ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
          required
        />
        {errors.city && (
          <p className="mt-1 text-sm text-red-600">{errors.city}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
        <select
          value={billingAddress.state}
          onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
          disabled={!billingAddressDifferent}
          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.state ? 'border-red-500' : 'border-gray-300'
            } ${!billingAddressDifferent ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
        >
          <option value="">Select State (Optional)</option>
          {states && states.length > 0 ? (
            states.map((state) => (
              <option key={state.state_code} value={state.state_code}>
                {state.state_name}
              </option>
            ))
          ) : (
            <option value="" disabled>Loading states...</option>
          )}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Zipcode*</label>
        <input
          type="text"
          value={billingAddress.zip_code}
          disabled={!billingAddressDifferent}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 6)
            setBillingAddress({ ...billingAddress, zip_code: digits })
          }}
          className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.zip_code ? 'border-red-500' : 'border-gray-300'
            } ${!billingAddressDifferent ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
          required
        />
        {errors.zip_code && (
          <p className="mt-1 text-sm text-red-600">{errors.zip_code}</p>
        )}
      </div>
    </div>
  </div>

  {/* Card Details */}
  <div className="mt-8 space-y-6">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Card number*</label>
      <div className="relative">
        <input
          type="text"
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
          placeholder="Card number"
          className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.cardNumber ? 'border-red-500' : 'border-gray-300'
            }`}
          required
        />
        {(() => {
          const cardDigits = cardNumber.replace(/\D/g, '');
          const isCompleteAndValid =
            cardDigits.length >= 13 &&
            cardDigits.length <= 19 &&
            !errors.cardNumber;
          return isCompleteAndValid ? (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            </div>
          ) : null;
        })()}
      </div>
      {errors.cardNumber && (
        <p className="mt-1 text-sm text-red-600">{errors.cardNumber}</p>
      )}
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Valid Thru</label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Month*</label>
            <select
              value={expiryMonth}
              onChange={(e) => setExpiryMonth(e.target.value)}
              className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.expiryMonth ? 'border-red-500' : 'border-gray-300'
                }`}
              required
            >
              <option value="">MM</option>
              {monthOptions.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            {errors.expiryMonth && (
              <p className="mt-1 text-xs text-red-600">{errors.expiryMonth}</p>
            )}
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Year*</label>
            <select
              value={expiryYear}
              onChange={(e) => setExpiryYear(e.target.value)}
              className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.expiryYear ? 'border-red-500' : 'border-gray-300'
                }`}
              required
            >
              <option value="">YYYY</option>
              {yearOptions.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>
            {errors.expiryYear && (
              <p className="mt-1 text-xs text-red-600">{errors.expiryYear}</p>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">CVV*</label>
        <div className="pt-[18px]">
          <input
            type="password"
            value={cvv}
            onChange={(e) => setCvv(formatCVV(e.target.value))}
            placeholder="CVV"
            maxLength={4}
            className={`w-full px-4 py-2.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.cvv ? 'border-red-500' : 'border-gray-300'
              }`}
            required
          />
        </div>
        {errors.cvv && (
          <p className="mt-1 text-xs text-red-600">{errors.cvv}</p>
        )}
      </div>
    </div>
  </div>

  {/* Information Text */}
  <div className="mt-6 space-y-2 text-sm text-gray-700">
    <p>
      Liberty Rent accepts the cards shown above for credit and debit card handling. We will notify you once we have received your payment.
    </p>
    <p>
      <strong>Note:</strong> The name on the card must match the name of the Applicant to avoid potential fraud. You can not use someone else's card.
    </p>
    <p>
      If you experience problems, contact our Customer Service on{' '}
      <a href="tel:8775817368" className="text-blue-600 hover:text-blue-800">
        877-581-7368
      </a>
    </p>
  </div>

  {/* Submit Buttons */}
  <div className="flex flex-wrap gap-4 mt-8">
    <button
      type="submit"
      disabled={isLoading}
      className="px-8 py-3 bg-[#1597ff] text-white font-semibold rounded-lg hover:bg-[#0d7acc] transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isLoading ? 'Processing...' : 'Pay Now'}
    </button>
    <button
      type="button"
      onClick={() => window.history.back()}
      className="px-8 py-3 bg-white text-gray-700 border-2 border-gray-300 font-medium rounded-lg hover:bg-gray-50 transition-colors"
    >
      Cancel
    </button>
  </div>
</form>
  )
}
